import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import type {
  ChartDataRange,
  KeyData,
  SeriesData,
  SeriesMetadata
} from '@/components/Chart/types/chartData';
import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { findSeriesBasedOnKey } from '@/components/Chart/utils/findSeriesBasedOnKey';
import { getFieldDecimalPlaces } from '@/components/Chart/utils/getFieldDecimalPlaces';
import { getSeriesId } from '@/components/Chart/utils/getSeriesId';
import { getSeriesName } from '@/components/Chart/utils/getSeriesName';
import { getShortTitle } from '@/components/Chart/utils/getShortTitle';
import { parseSeasonalSeriesDataToSeries } from '@/components/Chart/utils/parseSeasonalSeriesDataToSeries';
import { chartOptions } from '@/features/Charts/api';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';
import getDecimalPlaces from '@/utils/getDecimalPlaces';

import { buildChartLineSeries, buildChartRangeSeries } from '../utils/buildChartLineSeries';

interface ParseDataSeriesParams {
  symbolIndex: number;
  series: SeriesData;
  fieldIndex: number;
  metadata: SeriesMetadata;
  field: string;
  shortTitle: string | undefined;
  key: KeyData;
}

function parseDataSeries({
  symbolIndex,
  series,
  fieldIndex,
  metadata,
  field,
  shortTitle,
  key
}: ParseDataSeriesParams) {
  const combinedIndex = symbolIndex * Object.keys(series).length + fieldIndex;
  const decimalPlaces = metadata.Increment
    ? getDecimalPlaces(metadata.Increment)
    : getFieldDecimalPlaces(series?.[field]);
  const name = getSeriesName(shortTitle, metadata.Title);
  const id = getSeriesId(key, field);
  const dataRange = Object.keys(series)
    .map(Number)
    .filter(field => field && !Number.isNaN(field));

  const min = Math.min(...dataRange);
  const max = Math.max(...dataRange) - 1;
  const context = {
    ...key,
    ...metadata,
    decimalPlaces,
    combinedIndex,
    field
  };
  const timeRef = key.TimeRef;
  const timezone = metadata.TimeZone;

  const timeSeriesPoints = parseSeasonalSeriesDataToSeries({
    series,
    field,
    decimalPlaces,
    timeRef,
    timezone
  });

  if (field === 'range') {
    if (min >= max) return null;
    return buildChartRangeSeries({
      id: `${min}-${max} ${id} `,
      name: `${min}-${max} ${name} `,
      timeSeriesPoints,
      context
    });
  } else if (field === 'average') {
    if (min >= max) return null;
    return buildChartLineSeries({
      id: `Avg ${min}-${max} ${id}`,
      name: `Avg ${min}-${max} ${name}`,
      timeSeriesPoints,
      context
    });
  }
  return buildChartLineSeries({
    id: (field ? `${field} ` : '') + id,
    name: (field ? `${field} ` : '') + name,
    timeSeriesPoints,
    context,
    visible: fieldIndex > Object.keys(series).length - 5
  });
}

const useSeasonalChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  chartDataRange: ChartDataRange,
  isInViewport = true
) => {
  const {
    data: seasonalChartData,
    isError: isSeasonalChartDataError,
    isLoading: isSeasonalChartDataLoading
  } = useQuery({
    ...chartOptions.seasonal(templateChartSeries, chartDataRange),
    enabled: templateChartSeries !== undefined && !!chartDataRange?.from && isInViewport
  });

  const parseSeasonalDataToChartSeries = useCallback(
    ({
      series,
      symbolIndex,
      key,
      metadata
    }: {
      series: SeriesData;
      symbolIndex: number;
      key: KeyData;
      metadata: SeriesMetadata;
    }) => {
      const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
      const shortTitle = getShortTitle(templateSeries);
      return Object.keys(series)
        .map((field: string, fieldIndex) => {
          return parseDataSeries({
            symbolIndex,
            series,
            fieldIndex,
            metadata,
            field,
            shortTitle,
            key
          });
        })
        .filter((s): s is ExtendedSeriesOptionsType => s !== null);
    },
    [templateChartSeries]
  );
  const chartSeries = useMemo(() => {
    if (seasonalChartData?.symbols)
      return seasonalChartData.symbols.flatMap(({ key, series, metadata }, symbolIndex) =>
        parseSeasonalDataToChartSeries({ series, symbolIndex, key, metadata })
      );
  }, [seasonalChartData, parseSeasonalDataToChartSeries]);

  return {
    loading:
      isSeasonalChartDataLoading || !templateChartSeries || (!isInViewport && !seasonalChartData),
    error: isSeasonalChartDataError,
    chartSeries
  };
};

export default useSeasonalChartSeries;
